import { Box } from "@mui/material";
import Markdown from "markdown-to-jsx";
import { FC, ReactElement, useCallback } from "react";
import { useThemeContext } from "../../../globalContext/global-context";
import type { HintsProps } from "../../../util/types";
import { typoStyle } from "./style";
import { useAdaptiveTextFit } from "./utils";

export const ExternalLink: FC<
  { children: React.ReactNode } & React.AnchorHTMLAttributes<HTMLAnchorElement>
> = ({ children, ...props }) => (
  <a {...props} target="_blank" rel="noopener noreferrer">
    {children}
  </a>
);

const markdownPatterns = [
  /(^|\n)#{1,6}\s+\S/m,
  /(^|\n)(?:[-*+]|\d+\.)\s+\S/m,
  /\*\*[^*\n]+\*\*/,
  /(^|[^\w])_[^_\n]+_(?=[^\w]|$)/,
  /\[[^\]]+\]\([^)]+\)/,
  /`[^`\n]+`/,
  /(^|\n)>\s+\S/m,
  /(^|\n)```/m,
];

const containsMarkdownSyntax = (value: string): boolean =>
  markdownPatterns.some((pattern) => pattern.test(value));

const shouldUseMarkdownAlignment = (value: string): boolean => {
  if (containsMarkdownSyntax(value)) return true;

  // Survey prompts are commonly plain multi-line text and should stay left-aligned.
  if (value.includes("\n")) return true;

  // Preserve the previous fallback for long plain text blocks.
  return value.length > 100;
};

const Hints: FC<HintsProps> = ({
  text,
  sx,
  keyVal,
  testId,
  variant = "hint",
  onClick,
  adaptiveText,
}): ReactElement => {
  const { globalObject } = useThemeContext();
  const theme = globalObject.themeData.normalizedTheme;
  const aligningContent = useCallback(() => {
    if (text == undefined) return false;
    const trimText = text.toString().trim();

    return shouldUseMarkdownAlignment(trimText);
  }, [text]);
  const style = typoStyle(aligningContent(), theme, variant);
  const smartTextProcessor = (text: string): string => {
    if (!text) return "";

    const rawText = text.toString();

    if (containsMarkdownSyntax(rawText)) {
      // This is Markdown only trailing/leading whitespace removing
      return rawText.trim();
    }

    // Plain text - trim leading/trailing whitespace and limit leading indentation.
    // Lines with 4 or more leading spaces are limited to 3 spaces.
    return rawText
      .split("\n")
      .map((line) => {
        const match = line.match(/^(\s*)(.*)/);
        const spaces = match?.[1] || "";
        const content = match?.[2] || "";

        // Limit leading spaces to 3 if there are 4 or more
        const limitedSpaces = spaces.length >= 4 ? "   " : spaces;
        return limitedSpaces + content;
      })
      .join("\n")
      .trim();
  };
  const processedText = smartTextProcessor(text?.toString() ?? "");
  const {
    adaptiveStyle,
    elementRef,
    enabled: adaptiveEnabled,
    fontSize,
    group,
  } = useAdaptiveTextFit({
    adaptiveText,
    contentKey: processedText,
    typography: theme.tokens.typography,
  });

  return (
    <Box
      ref={elementRef}
      sx={[
        style,
        ...(sx ? (Array.isArray(sx) ? sx : [sx]) : []),
        { fontFamily: theme.tokens.typography.fontFamily },
        adaptiveStyle,
      ]}
      key={keyVal}
      data-testid={testId}
      data-fitted-font-size={adaptiveEnabled ? fontSize : undefined}
      data-adaptive-text-group={adaptiveEnabled ? group : undefined}
      role={variant === "error" ? "alert" : undefined}
      onClick={onClick}
    >
      <Markdown
        options={{
          overrides: {
            code: {
              props: {
                style: {
                  whiteSpace: "normal",
                },
              },
            },
            img: {
              props: {
                className: "custom-image", // Apply custom Material-UI styles
              },
            },
            a: {
              component: ExternalLink,
            },
          },
          disableParsingRawHTML: true,
        }}
      >
        {processedText}
      </Markdown>
    </Box>
  );
};

export default Hints;
